import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@resource-kit/ui/react';
import { usePermissions } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { http, ApiError } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import { useDispatchPreview } from '../hooks/useDispatchPreview';
import { useUndatedOrders, type UndatedOrder } from '../hooks/useUndatedOrders';

const ROW =
  'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3.5 border-t px-4 py-2.5 lg:grid-cols-[minmax(150px,auto)_minmax(0,1fr)_auto]';

export interface UndatedOrdersSectionProps {
  /** The day the pane shows; 「排到这一天」 schedules into it. */
  date: string;
}

/**
 * The 「无配送日订单 (Undated Order)」 pool in the awaiting-dispatch pane
 * (#2561, ADR-0042). Every day shows the same pool; an order joins this day
 * only when the operator schedules it. Hidden while the pool is empty.
 *
 * A failed row stays with its error and a retry button: the retry is safe —
 * the backend answers a same-date repeat by only creating the missing job.
 * The retry posts the date the row was submitted for, not the day now in
 * view: the first attempt may already have written that date.
 */
export function UndatedOrdersSection({ date }: UndatedOrdersSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canWrite = usePermissions().includes(PERMISSIONS.DISPATCH_WRITE);
  const pool = useUndatedOrders();
  // Same cached query as the run card; its cutoff flag is the B1 hint.
  const preview = useDispatchPreview(date);
  // A failed row keeps its own copy and its submitted date: if the date was
  // written but the job was not, the order has already left the pool, and the
  // retry must stay reachable and repeat that same date.
  const [failed, setFailed] = useState<
    Record<string, { order: UndatedOrder; date: string; detail: string }>
  >({});
  const [busy, setBusy] = useState(false);

  const pooled = pool.data ?? [];
  const pooledIds = new Set(pooled.map((order) => order.order_id));
  const orders = [
    ...pooled,
    ...Object.values(failed)
      .map((entry) => entry.order)
      .filter((order) => !pooledIds.has(order.order_id)),
  ];
  if (orders.length === 0) return null;

  const scheduleOne = async (order: UndatedOrder): Promise<void> => {
    const orderId = order.order_id;
    const target = failed[orderId]?.date ?? date;
    try {
      await http.post(`/api/dispatch/undated-orders/${encodeURIComponent(orderId)}/schedule`, {
        date: target,
      });
      setFailed(({ [orderId]: _done, ...rest }) => rest);
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : '';
      setFailed((prev) => ({ ...prev, [orderId]: { order, date: target, detail } }));
    }
  };

  // One request at a time, so a failure stays on its own row.
  const schedule = async (targets: UndatedOrder[]): Promise<void> => {
    setBusy(true);
    try {
      for (const order of targets) await scheduleOne(order);
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.dispatch.all() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dispatchJobs.all() });
    }
  };

  const pastCutoff = preview.data?.past_dispatch_cutoff === true;
  const summary = (order: UndatedOrder) =>
    order.items.map((item) => `${item.title} × ${item.quantity}`).join(' · ');

  return (
    <section data-testid="dispatch-undated-orders" className="rounded-lg border">
      <div className="flex flex-wrap items-center gap-2.5 bg-muted px-4 py-[11px]">
        <h3 className="text-[14.5px] font-semibold">{t('dispatch.undated.title')}</h3>
        <Badge className="border-transparent bg-warning/20 text-warning">
          {t('dispatch.undated.count', { count: orders.length })}
        </Badge>
        <span className="text-[11.5px] text-muted-foreground">
          {t('dispatch.undated.subtitle')}
        </span>
        {canWrite ? (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7 px-2.5 text-xs"
            data-testid="dispatch-undated-schedule-all"
            disabled={busy}
            onClick={() => void schedule(orders)}
          >
            {t('dispatch.undated.scheduleAll')}
          </Button>
        ) : null}
      </div>
      {pastCutoff ? (
        <p
          data-testid="dispatch-undated-cutoff-hint"
          className="border-t px-4 py-2 text-[13px] text-warning"
        >
          {t('dispatch.undated.pastCutoff', { at: preview.data?.dispatch_cutoff_time })}
        </p>
      ) : null}
      {orders.map((order) => {
        const error = failed[order.order_id]?.detail;
        return (
          <div
            key={order.order_id}
            data-testid={`dispatch-undated-order-${order.order_id}`}
            className={ROW}
          >
            <span className="font-medium">
              {order.order_name}
              <span className="ml-2 text-[13px] font-normal text-muted-foreground">
                {order.customer_name}
              </span>
            </span>
            <span className="col-span-full order-3 text-[13px] text-muted-foreground lg:order-none lg:col-auto">
              {summary(order)}
              {error !== undefined ? (
                <span
                  role="alert"
                  data-testid={`dispatch-undated-error-${order.order_id}`}
                  className="ml-2 text-destructive"
                >
                  {t('dispatch.undated.failed')} {error}
                </span>
              ) : null}
            </span>
            {canWrite ? (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-xs"
                data-testid={`dispatch-undated-schedule-${order.order_id}`}
                disabled={busy}
                onClick={() => void schedule([order])}
              >
                {error !== undefined ? t('dispatch.undated.retry') : t('dispatch.undated.schedule')}
              </Button>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
